/**
 * Shared "Quick Add Customer" dialog used from document forms (Sales Order, Estimate)
 * where a full guided flow would be overkill - only truly backend-mandatory fields
 * are required, with an optional collapsible address section.
 */
import React, { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Collapse,
} from '@mui/material'
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { CustomerService } from '../../services/customerService'
import { AddressService } from '../../services/addressService'
import { AddressType } from '../../types/address'
import { CustomerType } from '../../types/customer'
import { isValidMobile, isValidGSTIN, isValidPincode } from '../../utils/customerValidation'

interface CustomerGroupOption {
  id: string
  name: string
}

interface CustomerQuickAddDialogProps {
  open: boolean
  onClose: () => void
  customerGroups: CustomerGroupOption[]
  onCreated: (customer: any) => void
}

const initialState = {
  name: '',
  mobile_no: '',
  email: '',
  customer_type: CustomerType.RETAIL as string,
  customer_group_id: '',
  gstin: '',
  address_line1: '',
  city: '',
  state: '',
  pincode: '',
}

export const CustomerQuickAddDialog: React.FC<CustomerQuickAddDialogProps> = ({
  open,
  onClose,
  customerGroups,
  onCreated,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [form, setForm] = useState(initialState)
  const [addressOpen, setAddressOpen] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [creating, setCreating] = useState(false)

  const update = (field: keyof typeof initialState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const handleClose = () => {
    if (creating) return
    setForm(initialState)
    setAddressOpen(false)
    setErrors({})
    onClose()
  }

  const handleCreate = async () => {
    const newErrors: Record<string, string> = {}
    if (!form.name.trim()) newErrors.name = 'Name is required'
    if (!form.mobile_no.trim()) newErrors.mobile_no = 'Mobile number is required'
    else if (!isValidMobile(form.mobile_no)) newErrors.mobile_no = 'Invalid mobile number (10 digits, starts with 6-9)'
    if (form.gstin && !isValidGSTIN(form.gstin)) newErrors.gstin = 'Invalid GSTIN format (e.g., 22AAAAA0000A1Z5)'

    const hasAnyAddressField = Boolean(form.address_line1 || form.city || form.state || form.pincode)
    if (hasAnyAddressField) {
      if (!form.address_line1.trim()) newErrors.address_line1 = 'Address line 1 is required'
      if (!form.city.trim()) newErrors.city = 'City is required'
      if (!form.state.trim()) newErrors.state = 'State is required'
      if (!form.pincode.trim()) newErrors.pincode = 'Pincode is required'
      else if (!isValidPincode(form.pincode)) newErrors.pincode = 'Pincode must be 6 digits'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      if (hasAnyAddressField && (newErrors.address_line1 || newErrors.city || newErrors.state || newErrors.pincode)) {
        setAddressOpen(true)
      }
      return
    }

    setCreating(true)
    try {
      const created = await CustomerService.createCustomer({
        name: form.name,
        billing_name: form.name,
        mobile_no: form.mobile_no,
        email: form.email || `${form.mobile_no}@dhanshika.in`,
        whatsapp_no: form.mobile_no,
        customer_type: form.customer_type as any,
        customer_group_id: form.customer_group_id || undefined,
        gstin: form.gstin || undefined,
        is_gst_registered: Boolean(form.gstin),
      } as any)

      if (hasAnyAddressField) {
        try {
          await AddressService.createAddress({
            customer_id: created.id,
            address_type: AddressType.BOTH,
            address_line1: form.address_line1,
            city: form.city,
            state: form.state,
            postal_code: form.pincode,
            country: 'India',
            is_default: true,
          })
        } catch {
          enqueueSnackbar(
            "Customer created, but the address couldn't be saved. Add it from Party Management.",
            { variant: 'warning' }
          )
        }
      }

      enqueueSnackbar('Customer created successfully', { variant: 'success' })
      onCreated(created)
      handleClose()
    } catch (error: any) {
      const msg = error?.response?.data?.detail || 'Failed to create customer'
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid #e0e0e0', pb: 1.5 }}>
        Add New Customer (Quick)
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Customer Name"
              size="small"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              error={Boolean(errors.name)}
              helperText={errors.name}
              placeholder="Full Name"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Mobile Number"
              size="small"
              value={form.mobile_no}
              onChange={(e) => update('mobile_no', e.target.value.replace(/\D/g, '').slice(0, 10))}
              inputProps={{ maxLength: 10 }}
              error={Boolean(errors.mobile_no)}
              helperText={errors.mobile_no || '10-digit number, starts with 6-9'}
              placeholder="10-digit number"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email (Optional)"
              size="small"
              type="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              placeholder="email@example.com"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Customer Type</InputLabel>
              <Select
                value={form.customer_type}
                onChange={(e) => update('customer_type', e.target.value)}
                label="Customer Type"
              >
                <MenuItem value={CustomerType.RETAIL}>Retail</MenuItem>
                <MenuItem value={CustomerType.RESELLER}>Reseller</MenuItem>
                <MenuItem value={CustomerType.WHOLESALER}>Wholesaler</MenuItem>
                <MenuItem value={CustomerType.BROKER}>Broker</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Customer Group (Optional)</InputLabel>
              <Select
                value={form.customer_group_id}
                onChange={(e) => update('customer_group_id', e.target.value)}
                label="Customer Group (Optional)"
              >
                <MenuItem value="">
                  <em>None - auto-assign to Retail</em>
                </MenuItem>
                {customerGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="GSTIN (Optional)"
              size="small"
              value={form.gstin}
              onChange={(e) => update('gstin', e.target.value.toUpperCase())}
              error={Boolean(errors.gstin)}
              helperText={errors.gstin || 'Leave blank if not GST registered'}
              inputProps={{ maxLength: 15 }}
              placeholder="22AAAAA0000A1Z5"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              size="small"
              onClick={() => setAddressOpen(prev => !prev)}
              endIcon={addressOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none' }}
            >
              Add address (optional)
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Collapse in={addressOpen} sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    size="small"
                    value={form.address_line1}
                    onChange={(e) => update('address_line1', e.target.value)}
                    error={Boolean(errors.address_line1)}
                    helperText={errors.address_line1}
                    placeholder="Street address / Shop No."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    size="small"
                    value={form.city}
                    onChange={(e) => update('city', e.target.value)}
                    error={Boolean(errors.city)}
                    helperText={errors.city}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    size="small"
                    value={form.state}
                    onChange={(e) => update('state', e.target.value)}
                    error={Boolean(errors.state)}
                    helperText={errors.state}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    size="small"
                    value={form.pincode}
                    onChange={(e) => update('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6 }}
                    error={Boolean(errors.pincode)}
                    helperText={errors.pincode}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Button onClick={handleClose} color="inherit" disabled={creating}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={creating}
          startIcon={creating ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {creating ? 'Creating...' : 'Create & Select'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CustomerQuickAddDialog
